import type { FC } from 'react';

import { useEffect } from 'react';
import Page from '@conekta/cronos/declarative/Page';
import { useTranslation } from '@conekta/cronos/i18n';
import { Link } from 'app/components/Link';
import { ChangeLanguage } from 'app/pages/hosted-checkout/components/ChangeLanguage';
import OrderDetail from 'app/pages/hosted-checkout/components/OrderDetail';
import 'app/pages/hosted-checkout/hosted-checkout.scss';
import 'app/pages/hosted-checkout/views/Checkout/styles.scss';
import Logo from 'app/pages/hosted-checkout/components/Logo';
import { SharableMetadata } from 'app/pages/hosted-checkout/components/SharableMetadata';
import { SidebarWrapper } from 'app/pages/hosted-checkout/components/SidebarWrapper';
import { TestBar } from 'app/pages/hosted-checkout/components/TestBar';
import { bootCheckoutIframe } from 'app/pages/hosted-checkout/helpers/bootIframe';
import { ViewState, IEvent } from 'app/pages/hosted-checkout/interface';
import { sendAnalytic } from 'app/util/analytics';
import { AnalyticEvents, PaymentMethodType } from 'app/util/constants';
import { TFunction } from 'i18next';

const iframeIdentifier = 'conektaIframeContainer';
const iframeMinHeight = 600;
const root = `iFrame.confirmation.${PaymentMethodType.Card.toString().toLowerCase()}`;

export const onEventListener = (event: IEvent, t: TFunction) => {
  const { name: eventName, value } = event;
  if (!value) {
    const element = document.getElementsByClassName('top__monthly-installments')[0];

    if (element) {
      element.innerHTML = '';
    }
  }
  if (eventName === 'monthlyInstallmentSelected' && value) {
    const text = t(`${root}.monthlyInstallmentsInformation`, {
      monthlyInstallments: value.monthlyInstallments,
    });
    document.getElementsByClassName('top__monthly-installments')[0].innerHTML = value
      ? `${text} <span>$${value.monthlyFee}</span>`
      : '';
  }
};

export const Checkout: FC<ViewState> = (state) => {
  const { entity, checkoutRequest, keys, isIntegration, configuration = {} } = state;
  const { id, companyId, type } = checkoutRequest;
  const { t, i18n } = useTranslation();

  const onHeightChange = (height: number) => {
    const iframeContainer = document.getElementById(`${iframeIdentifier}`);
    if (!iframeContainer) {
      return;
    }
    iframeContainer.style.height = `${height > iframeMinHeight ? height : iframeMinHeight}px`;
  };

  useEffect(() => {
    bootCheckoutIframe({
      targetIFrame: `#${iframeIdentifier}`,
      checkoutRequestId: checkoutRequest.id,
      publicKey: keys.publicKey,
      onEventListener: (event) => onEventListener(event, t),
      isIntegration,
      locale: i18n.language,
      preloadedState: state,
      customStyle: configuration.customStyleJSON,
      onHeightChange,
    });
  }, []);

  const sendAnalyticHelpCenter = () => {
    sendAnalytic(AnalyticEvents.helpCenter, id, companyId, type);
  };

  const { sidebar: sidebarStyles } = configuration.customStyleJSON ?? {};

  return (
    <Page state={state}>
      <SharableMetadata merchantName={entity.name} paymentReason={checkoutRequest.name} />
      <div className='main-wrapper'>
        <div className='main-wrapper__IFrame'>
          {!checkoutRequest.liveMode && <TestBar />}
          <div className='main-wrapper__IFrameInner'>
            <div className='conekta-iframe-container'>
              <div id={iframeIdentifier} />
            </div>
          </div>
        </div>
        <SidebarWrapper {...sidebarStyles} className='main-wrapper__Checkout'>
          <OrderDetail
            name={checkoutRequest.name}
            amount={checkoutRequest.amount}
            orderTemplate={checkoutRequest.orderTemplate}
          >
            <Logo theme={configuration.customStyleJSON?.styles} merchantName={entity.name} />
          </OrderDetail>
          <div className='checkout-footer'>
            <Link
              {...sidebarStyles}
              href='https://help.conekta.com/hc/es-419/sections/360007368253-Link-de-Pago'
              target='_blank'
              rel='noreferrer'
              onClick={() => sendAnalyticHelpCenter()}
            >
              {t('hostedCheckout.common.helpCenter')}
            </Link>
            <span className='checkout-footer__separator'>|</span>
            <ChangeLanguage id={id} companyId={companyId} type={type} />
          </div>
        </SidebarWrapper>
      </div>
    </Page>
  );
};
